import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, Zap, Coins } from "lucide-react";
import { toast } from "sonner";
import { http } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { TIERS, TOPUPS } from "../lib/pricing";

export const UpgradeDialog = ({ open, onOpenChange }) => {
  const { user, credits } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState("");
  const [code, setCode] = useState("");

  const requireAccount = () => {
    if (!user) {
      onOpenChange(false);
      navigate("/register");
      return true;
    }
    return false;
  };

  const checkout = async (lookup_key) => {
    if (requireAccount()) return;
    setLoading(lookup_key);
    try {
      const { data } = await http.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Try again.");
      setLoading("");
    }
  };

  const redeem = async () => {
    if (requireAccount()) return;
    setLoading("redeem");
    try {
      await http.post("/account/redeem", { code: code.trim() });
      toast.success("Lifetime access unlocked.");
      setCode("");
      onOpenChange(false);
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code.");
    } finally {
      setLoading("");
    }
  };

  const cancelPlan = async () => {
    setLoading("cancel");
    try {
      const { data } = await http.post("/account/cancel");
      toast.success(data.message);
      onOpenChange(false);
    } catch {
      toast.error("Could not cancel.");
    } finally {
      setLoading("");
    }
  };

  const currentPlan = credits?.plan || "free";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="upgrade-dialog" className="bg-[#F8F6F1] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#1B3A4B]">Plans & coins</DialogTitle>
          <DialogDescription className="text-[#5C564E]">
            1 coin = 1 price search. Plans refill every month. Extra coins are $0.50 each.
          </DialogDescription>
        </DialogHeader>

        {credits?.lifetime ? (
          <div className="rounded-xl border-2 border-[#A6844A] bg-white/70 p-6 text-center mt-2">
            <div className="flex items-center justify-center gap-2 text-[#1B3A4B]">
              <Zap className="w-5 h-5 text-[#A6844A]" />
              <span className="font-serif text-2xl font-bold">Lifetime Pro</span>
            </div>
            <p className="text-sm text-[#5C564E] mt-2">Unlimited searches, every template, clean PDFs. No billing — ever.</p>
          </div>
        ) : (
          <>
            {user && (
              <div data-testid="coin-balance" className="flex items-center gap-2 rounded-xl bg-white/70 border border-[#E2DCD2] px-4 py-3 mt-1">
                <Coins className="w-5 h-5 text-[#A6844A]" />
                <span className="text-sm text-[#1B3A4B] font-semibold">
                  {credits?.coins ?? 0} coins left{credits?.topup_coins > 0 ? ` · +${credits.topup_coins} top-up` : ""}
                </span>
                <span className="ml-auto text-xs text-[#5C564E] capitalize">{currentPlan} plan</span>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
              {TIERS.map((t) => {
                const isCurrent = currentPlan === t.key;
                return (
                  <div key={t.key} data-testid={`tier-${t.key}`} className={`rounded-xl border p-4 flex flex-col ${isCurrent ? "border-[#A6844A] bg-white" : "border-[#E2DCD2] bg-white/70"}`}>
                    <div className="font-semibold text-[#1B3A4B]">{t.name}</div>
                    <div className="font-serif text-2xl font-bold text-[#1C1915] mt-1">
                      {t.price}<span className="text-xs font-sans font-normal text-[#5C564E]">/mo</span>
                    </div>
                    <div className="mt-2 text-sm text-[#5C564E] flex items-center gap-1">
                      <Coins className="w-3.5 h-3.5 text-[#A6844A]" /> {t.coins} coins
                    </div>
                    <div className="mt-1 text-xs text-[#5C564E] flex items-center gap-1">
                      <Check className="w-3 h-3 text-[#2E6B48]" /> {t.perk}
                    </div>
                    <div className="mt-3">
                      {isCurrent ? (
                        <span className="text-xs px-2.5 py-1 rounded-full bg-[#E7D7B8] text-[#1B3A4B] font-semibold">Current</span>
                      ) : t.lookup ? (
                        <Button
                          data-testid={`choose-${t.key}`}
                          onClick={() => checkout(t.lookup)}
                          disabled={!!loading}
                          className="w-full rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white h-9 text-sm"
                        >
                          {loading === t.lookup ? <Loader2 className="w-4 h-4 animate-spin" /> : "Choose"}
                        </Button>
                      ) : (
                        <span className="text-xs text-[#5C564E]">Default</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="rounded-xl border border-[#E2DCD2] bg-white/70 p-5 mt-2">
              <div className="flex items-center gap-2 text-[#1B3A4B] font-semibold">
                <Coins className="w-4 h-4 text-[#A6844A]" /> Top up coins — $0.50 each
              </div>
              <p className="text-sm text-[#5C564E] mt-1">Extra searches past your monthly plan. They never reset.</p>
              <div className="grid grid-cols-3 gap-2 mt-3">
                {TOPUPS.map((tp) => (
                  <Button
                    key={tp.lookup}
                    data-testid={`topup-${tp.coins}`}
                    onClick={() => checkout(tp.lookup)}
                    disabled={!!loading}
                    variant="outline"
                    className="rounded-full border-[#A6844A] text-[#1B3A4B] h-10 text-sm"
                  >
                    {loading === tp.lookup ? <Loader2 className="w-4 h-4 animate-spin" /> : `${tp.coins} · ${tp.price}`}
                  </Button>
                ))}
              </div>
            </div>

            {["starter", "pro", "heavy"].includes(currentPlan) && !credits?.subscription_cancel && (
              <Button data-testid="cancel-plan-button" onClick={cancelPlan} disabled={!!loading} variant="ghost" className="w-full text-[#9E2A2B] hover:bg-[#9E2A2B]/10">
                {loading === "cancel" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancel plan (keeps current coins)"}
              </Button>
            )}
            {credits?.subscription_cancel && (
              <p className="text-xs text-[#5C564E] text-center">Set to not renew — drops to Free at period end.</p>
            )}

            <div className="rounded-xl border border-[#E2DCD2] bg-white/70 p-5 mt-1">
              <div className="text-sm font-semibold text-[#1B3A4B] mb-2">Have an access code?</div>
              <div className="flex gap-2">
                <Input data-testid="redeem-code-input" placeholder="Enter code" value={code} onChange={(e) => setCode(e.target.value)} />
                <Button data-testid="redeem-code-button" onClick={redeem} disabled={!!loading || !code.trim()} className="rounded-full bg-[#A6844A] hover:bg-[#8f6f3b] text-white shrink-0 px-5">
                  {loading === "redeem" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Redeem"}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
